import { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import AlertIcon from '@material-ui/icons/ErrorOutline';
import Grid from '@material-ui/core/Grid';
import Modal from 'components/CncUI/Modal';
import Typography from 'components/CncUI/Typography';
import TextField from 'components/CncUI/TextField';
import TextFieldTaxId from 'components/CncUI/TextField/TextFieldTaxId';
import Dropdown from 'components/CncUI/Dropdown';
import Button from 'components/CncUI/Button';
import Alert from 'components/CncUI/Alert';
import Link from 'components/CncUI/Link';
import SearchFieldAddress from 'components/SearchFieldAddress';
import Loader from 'components/Loader';
import ToggleValue from 'components/ToggleValue';
import UploadDocument from '../UploadDocument';
import Attempts from '../Sidebar/Attempts';

import useData from 'hooks/useData';
import useMutation from 'hooks/useMutation';
import useQuery from 'hooks/useQuery';
import useCountry from 'hooks/useCountry';
import useSnackbar from 'hooks/useSnackbar';
import useFeatureFlags from 'hooks/useFeatureFlags';
import Logger from 'utilities/logger';

import parseAddress from '../utils/parseAddress';

const FLAG_LO_TAXID10 = 'MyCnC-LO-TaxId10';

const useStyles = makeStyles(({ palette }) => ({
  green: {
    color: palette.cncUI.default.primary,
  },
  red: {
    color: palette.cncUI.alert.red.primary,
  },
  icon: {
    verticalAlign: 'middle',
    cursor: 'pointer',
  },
  buttons: {
    marginTop: 16,
  },
  button: {
    width: 153,
  },
  modal: {
    paddingTop: '68px !important',
    paddingBottom: 68,
  },
}));

const EditPanel = props => {
  const classes = useStyles();
  const notify = useSnackbar();
  const logger = Logger('Loanout.EditPanel');
  const { country } = useCountry();
  const { isEnabled } = useFeatureFlags();
  const { cancel, reload, loanout = null, offer = false } = props;
  const [loadAttempts, fetchAttempts] = useState(true);
  const [attempts, setAttempts] = useState([]);
  const [show, showModal] = useState(false);
  const [review, setReview] = useState(false);
  const saveLoanout = useMutation('saveLoanout', {
    scopes: 'payroll_employee:edit',
    request: input => ({ input }),
    response: res => res.data.saveLoanout || false,
  });
  const getAttempts = useQuery('getLoanoutAttempts', {
    fetchPolicy: 'network-only',
    scopes: 'payroll_employee:edit',
    response: res => res.data.getLoanoutAttempts || [],
  });
  const [form, modify] = useData({
    source: 'profile',
    taxId: loanout?.taxId || '',
    name: loanout?.name || '',
    type: loanout?.type || '',
    address: loanout?.address ? parseAddress(loanout) : '',
    city: loanout?.city || '',
    state: loanout?.state || '',
    zipCode: loanout?.zipCode || '',
    w9: null,
    loi: null,
  });
  const maxTaxId = isEnabled(FLAG_LO_TAXID10) ? 10 : 9;

  useEffect(() => {
    if (loadAttempts) {
      getAttempts()
        .then(attempts => {
          setAttempts(attempts);
          fetchAttempts(false);
        })
        .catch(err => {
          logger.error(err);
          fetchAttempts(false);
        });
    }
  }, [loadAttempts]);

  const valid = () => {
    for (let key of ['taxId', 'name', 'type', 'address', 'w9', 'loi']) {
      if (!form[key]) {
        return false;
      }
    }
    return true;
  };

  const save = () => {
    setReview(false);
    showModal(true);
    saveLoanout(form)
      .then(verified => {
        logger.debug('Saved', verified);
        showModal(false);
        if (verified) {
          notify({
            type: 'success',
            title: 'Loan-out was successfully added',
            message: 'You just updated your loan-out information.',
          });
          reload();
        } else {
          notify({
            type: 'error',
            title: 'Loan-out could not be saved',
            message:
              'An error occurred trying to save your Loan-out information.',
          });
          fetchAttempts(true);
          modify({ loi: null, w9: null });
        }
      })
      .catch(err => {
        logger.error(err);
        notify({
          type: 'error',
          title: 'Loan-out could not be saved',
          message: err.message,
        });
        showModal(false);
        fetchAttempts(true);
        modify({ loi: null, w9: null });
      });
  };

  const rowValue = (title, value) => (
    <Grid item xs={12}>
      <Typography variant="caption" noMargin className={classes.green}>
        {title}
      </Typography>
      <Typography
        variant="body1"
        size="short"
        noMargin
        className={classes.value}
      >
        {value}
      </Typography>
    </Grid>
  );

  const attempt = attempts && attempts.length && attempts[0];
  const blocked = (attempts || []).filter(a => a.valid === false).length >= 2;
  const remaining = 2 - (attempts || []).filter(a => a.valid === false).length;

  const hardBlocked =
    (attempts || [])
      .filter(a => a.valid === false)
      .filter(a => a.error.includes('"type":"hard"')).length > 0;

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h4" className={classes.green} noMargin>
          {loanout ? 'Edit loan-out' : 'Add a loan-out'}
        </Typography>
      </Grid>
      <Grid item xs={12} lg={8}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="body2" size="short" noMargin>
                  {loanout ? (
                    <>
                      Editing an existing loan-out will require updating your
                      W-9 form and re-verification. Only one loan-out may be
                      associated with your profile. All fields are required.
                    </>
                  ) : (
                    <>
                      Add your loan-out information by filling in the form
                      below. Your loan-out information will be verified before
                      it can be applied to your profile. All fields are
                      required.
                    </>
                  )}
                </Typography>
              </Grid>
              {!offer && (
                <Grid item xs={12}>
                  <Grid container spacing={2}>
                    <Grid item style={{ width: 32 }}>
                      <AlertIcon />
                    </Grid>
                    <Grid item style={{ flex: 1 }}>
                      <Typography variant="body2" size="short" medium noMargin>
                        Adding a loan-out to your profile does not necessitate
                        that you will be paid as a loan-out on a given project.
                        Please work with your production.
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
              )}
              {(hardBlocked || blocked) && (
                <Grid item xs={12}>
                  <Alert error fullWidth>
                    <strong style={{ lineHeight: '20px' }}>Error</strong>
                    <Typography body1 short noMargin>
                      Your ability to add/edit a loan-out online has been locked
                      due to too many failed attempts or an invalid loan-out.
                      Please contact{' '}
                      <Link href="/crew-support">Employee Help Desk</Link> for
                      support.
                    </Typography>
                  </Alert>
                </Grid>
              )}
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextFieldTaxId
                  fullWidth
                  disabled={blocked || hardBlocked}
                  label="Tax-ID or FEIN"
                  value={form.taxId}
                  onChange={e => modify({ taxId: e.target.value })}
                  maxChars={maxTaxId}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  disabled={blocked || hardBlocked}
                  label="Corporation Name"
                  value={form.name}
                  onChange={e => modify({ name: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <Dropdown
                  fullWidth
                  disabled={blocked || hardBlocked}
                  placeholder="select a corporation type"
                  value={form.type}
                  label="Corporation Type"
                  onChange={e => modify({ type: e.target.value })}
                >
                  <Dropdown.Item value="S Corp">S Corp</Dropdown.Item>
                  <Dropdown.Item value="C Corp">C Corp</Dropdown.Item>
                  <Dropdown.Item value="LLC Single Ownership">
                    LLC Single Ownership
                  </Dropdown.Item>
                  <Dropdown.Item value="LLC Partnership">
                    LLC Partnership
                  </Dropdown.Item>
                  <Dropdown.Item value="Foreign">Foreign</Dropdown.Item>
                </Dropdown>
              </Grid>
              <Grid item xs={12}>
                <SearchFieldAddress
                  label="Corporation Address"
                  disabled={blocked || hardBlocked}
                  value={form.address}
                  onSelect={address =>
                    modify({
                      address: address.Line1,
                      city: address.City,
                      state: address.ProvinceCode,
                      zipCode: address.PostalCode,
                    })
                  }
                  onEmpty={() =>
                    modify({ address: '', city: '', state: '', zipCode: '' })
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <UploadDocument
                  template="W9"
                  value={form.w9}
                  onSave={w9 => modify({ w9 })}
                  disabled={blocked || hardBlocked}
                  country={country}
                />
              </Grid>
              <Grid item xs={12}>
                <UploadDocument
                  template="LOI"
                  value={form.loi}
                  onSave={loi => modify({ loi })}
                  disabled={blocked || hardBlocked}
                  country={country}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Grid
              container
              spacing={2}
              justify="flex-end"
              alignItems="center"
              className={classes.buttons}
            >
              <Grid item>
                <Button secondary className={classes.button} onClick={cancel}>
                  Cancel
                </Button>
              </Grid>
              <Grid item>
                <Button
                  loading={show}
                  disabled={!valid() || blocked || hardBlocked}
                  onClick={() => setReview(true)}
                  className={classes.button}
                >
                  Review
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12} lg={4}>
        {loadAttempts && <Loader />}
        {(attempt && <Attempts attempt={attempt} />) || ''}
      </Grid>
      <Modal open={review} minWidth={500} onClose={() => setReview(false)}>
        <Modal.Title onClose={() => setReview(false)}>
          Review Your Loan-out Info
        </Modal.Title>
        <Modal.Content>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="body2" size="short" noMargin>
                    Ensure the information below is correct before clicking
                    “Verify.” Information that does not match what’s registered
                    with your loan-out’s state government will fail.
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography
                    variant="body2"
                    size="short"
                    className={classes.red}
                    medium
                    noMargin
                  >
                    You have {remaining} attempt{remaining > 1 ? 's' : ''}{' '}
                    remaining.
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Grid container spacing={1}>
                {rowValue(
                  'Tax-ID or FEIN',
                  <ToggleValue
                    value={form.taxId}
                    hide={v => `***-**-${v.slice(-4)}`}
                  />,
                )}
                {rowValue('Corporation Name', form.name)}
                {rowValue('Corporation Type', form.type)}
                {rowValue('Corporation Address', parseAddress(form))}
              </Grid>
            </Grid>
          </Grid>
        </Modal.Content>
        <Modal.Actions style={{ marginTop: 16 }}>
          <Grid container spacing={2} justify="flex-end">
            <Grid item>
              <Button secondary onClick={() => setReview(false)}>
                Back to Edit
              </Button>
            </Grid>
            <Grid item>
              <Button onClick={save}>Verify</Button>
            </Grid>
          </Grid>
        </Modal.Actions>
      </Modal>
      <Modal open={show} minWidth={450}>
        <Modal.Content className={classes.modal}>
          <Typography variant="h5" align="center" noMargin>
            Please wait while we verify your information
          </Typography>
          <Loader
            message="This should take less than a minute"
            messageProps={{
              variant: 'body2',
              size: 'long',
              noMargin: true,
              style: { paddingBottom: 0 },
            }}
          />
        </Modal.Content>
      </Modal>
    </Grid>
  );
};

export default EditPanel;
